import {
  Banner,
  Button,
  FieldWrapper,
  FormField,
  FormGrid,
  Page,
  SelectionList,
  Tag,
} from '@dixa/lib-shared-habla';
import { isResponseWithData } from 'lib-shared-auth';
import { config } from 'lib-shared-backend-config';
import { ampli } from 'lib-shared-tracking';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';

import { ApiTokenAccessScope, ApiTokenVersion, authApi } from 'apis/auth';
import { HookFormSelectionList, HookFormText } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { validateName } from 'utils/formValidator';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { SettingsIntegrationsDocumentationButton } from '../../../../components/documentationButton/documentationButton';
import { pathToApiTokensSettings } from '../../settingsIntegrationsApiTokens.view';
import {
  apiTokenTypesByVersion,
  dixaApiToken,
  integrationsApiToken,
  SelectToken,
} from '../../utils/tokenType';

type ApiTokensCreateFormValues = {
  name: string;
  scopes: ApiTokenAccessScope[];
  version?: ApiTokenVersion[];
};

const defaultTokenVersion: ApiTokenVersion = '2';

/** Settings page to create a new API token. */
export const SettingsIntegrationsApiTokensCreateView = (): JSX.Element => {
  const navigate = useAppNavigate();
  const notify = useNotifications();

  const { allowIntegrationApiTokenUsage } = useFeatureFlag([
    'allow-integration-api-token-usage',
  ]);

  const apiTokenTypes: SelectToken[] = [
    dixaApiToken,
    ...(allowIntegrationApiTokenUsage ? [integrationsApiToken] : []),
  ];

  const [createApiToken, { isLoading: isCreating }] =
    authApi.useCreateApiTokenMutation();

  const { control, handleSubmit } = useForm<ApiTokensCreateFormValues>({
    defaultValues: {
      name: '',
      scopes: ['FULL_ACCESS'],
    },
    mode: 'onChange',
  });

  const selectedTokenVersion = useWatch({
    control: control,
    name: 'version',
  });

  const onSubmit: SubmitHandler<ApiTokensCreateFormValues> = async ({
    name,
    scopes,
    version,
  }) => {
    const response = await createApiToken({
      name,
      scopes,
      version:
        version && version?.length > 0 ? version[0] : defaultTokenVersion,
    }).unwrap();

    if (isResponseWithData(response)) {
      notify(
        {
          title: 'Success',
          text: `"${response.data?.name}" has been created.`,
          icon: 'i-done',
          time: 5000,
        },
        NotificationType.Success,
      );

      ampli.settingsIntegrationsApiTokensAddConfirm({
        apiTokenVersion: response.data.version,
      });

      navigate({
        path: `${pathToApiTokensSettings}/:id`,
        params: {
          path: {
            id: response.data.id,
          },
        },
      });
    } else {
      notify(
        {
          title: 'Error while creating the API token.',
        },
        NotificationType.Error,
      );
    }
  };

  return (
    <Page.Section
      title="Create API token"
      titleMeta={
        <SettingsIntegrationsDocumentationButton
          onClick={() =>
            window.open(
              `https://${config.domain.integrations}/openapi`,
              '_blank',
              'noopener,noreferrer',
            )
          }
        />
      }
      controls={
        <Button
          disabled={isCreating}
          isLoading={isCreating}
          onClick={handleSubmit(onSubmit)}
        >
          Save changes
        </Button>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormGrid>
          <FormField span={3}>
            <HookFormText
              control={control}
              label="Name"
              name={'name'}
              rules={{
                required: {
                  value: true,
                  message: 'Please add a name',
                },
                validate: validateName,
              }}
              componentProps={{
                placeholder: 'Insert name',
              }}
            />
          </FormField>
          <FormField span={3} />

          <FormField span={3}>
            {allowIntegrationApiTokenUsage ? (
              <HookFormSelectionList
                control={control}
                label="Version"
                name={'version'}
                rules={{
                  required: {
                    value: true,
                    message: 'Please select a version',
                  },
                }}
                meta={{
                  warning:
                    selectedTokenVersion?.[0] === '1'
                      ? 'You are about to create a token for a deprecated API'
                      : null,
                }}
                componentProps={{
                  clearable: true,
                  placeholder: 'Select token version',
                  getItemTitleByValue: (
                    version: ApiTokenVersion | null | undefined,
                  ) => (
                    <Tag
                      color={version && apiTokenTypesByVersion[version].color}
                    >
                      {version ? apiTokenTypesByVersion[version].label : null}
                    </Tag>
                  ),
                }}
              >
                {apiTokenTypes.map((token) => (
                  <SelectionList.Item key={token.type} value={token.version}>
                    <Tag color={token.color}>{token.label}</Tag>
                  </SelectionList.Item>
                ))}
              </HookFormSelectionList>
            ) : (
              <FieldWrapper label="Version">
                <Tag color={dixaApiToken.color}>{dixaApiToken.label}</Tag>
              </FieldWrapper>
            )}
          </FormField>
          <FormField span={3} />
        </FormGrid>
      </form>

      <Banner
        variant="announcement"
        headline="Note when creating a new API token"
        description={
          <>
            The new token is bound to the Administrator account that created it
            which gives integrations full access to your Dixa account. Be very
            careful when handling it and store it securely.
          </>
        }
      />
    </Page.Section>
  );
};
